import { useState } from 'react';

import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { JobTreeList } from '../inbox/JobTreeList.js';
import type { JobTreeNode } from '../inbox/job-tree.js';
import { plural } from '../util/format.js';
import styles from './WidgetJobFilter.module.css';

interface JobFilterButtonProps {
  /** Whether the committed picks narrow anything. Told, not derived: see `narrowingJobs`. */
  narrowed: boolean;
  open: boolean;
  onClick: () => void;
}

/** The widget home's filter trigger, in `/chat`'s tool-button skin: lit with a dot only
 *  while the scope actually narrows. */
export function JobFilterButton({ narrowed, open, onClick }: JobFilterButtonProps) {
  return (
    <button
      type="button"
      className={narrowed || open ? styles.toolBtnActive : styles.toolBtn}
      title="Filter"
      aria-label="Filter by job"
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={onClick}
    >
      <Icon name="filter" size={17} />
      {narrowed && <span className={styles.toolDot} data-testid="filter-dot" />}
    </button>
  );
}

interface JobFilterSheetProps {
  tree: JobTreeNode[];
  /** What the sheet shows ticked — the panel's draft, committed when it closes. */
  selected: string[];
  onChange: (jobIds: string[]) => void;
  onSelectMany: (ids: string[], on: boolean) => void;
  onClear: () => void;
  /** The sheet's only exit: ✕, the scrim and Show all commit the picks the same way. */
  onClose: () => void;
  /** Chats the picks would leave; null until that answer lands. */
  shownCount: number | null;
}

/** The posting tree as a sheet over the list. The tree is `/chat`'s own `JobTreeList` —
 *  the dialog's other facets want a column this box has not got, but the job cut is the same. */
export function JobFilterSheet({
  tree,
  selected,
  onChange,
  onSelectMany,
  onClear,
  onClose,
  shownCount,
}: JobFilterSheetProps) {
  const [query, setQuery] = useState('');

  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((held) => held !== id) : [...selected, id]);

  return (
    <div className={styles.overlay}>
      <div className={styles.scrim} data-testid="filter-scrim" onClick={onClose} />
      <div className={styles.sheet} role="dialog" aria-label="Filter by Job">
        <div className={styles.head}>
          <h2 className={styles.title}>Filter by Job</h2>
          <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <div className={styles.tools}>
          <label className={styles.search}>
            <Icon name="search" size={15} />
            <input
              className={styles.searchInput}
              type="search"
              value={query}
              placeholder="Job title or location"
              aria-label="Search job title or location"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>

        <div className={styles.options}>
          <JobTreeList
            tree={tree}
            needle={query.trim().toLowerCase()}
            selected={selected}
            onToggle={toggle}
            onSelectMany={onSelectMany}
            emptyNote="Nothing here is bound to a posting."
          />
        </div>

        <div className={styles.foot}>
          {/* Show stays live at zero: no results is when an employer most wants
              a way back to the list. */}
          <Button variant="ghost" disabled={selected.length === 0} onClick={onClear}>
            Clear all
          </Button>
          <Button onClick={onClose}>
            {shownCount === null ? 'Show chats' : `Show ${plural(shownCount, 'chat')}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
